/**
 * Path validation utilities for user-provided file and folder paths.
 *
 * Embedded devices (MicroPython/CircuitPython) have flat filesystems rooted at `/`.
 * These validations prevent path traversal attacks and invalid characters from
 * reaching the device via raw REPL commands.
 */

/**
 * Validate a user-provided file or folder path for safety.
 *
 * Rejects:
 * - Paths containing `..` (traversal)
 * - Paths with null bytes
 * - Empty paths
 * - Paths that resolve outside the device root
 *
 * @param path - The path to validate
 * @returns An object with `valid` boolean and optional `reason` string
 */
export function validateDevicePath(path: string): {
    valid: boolean;
    reason?: string;
} {
    if (!path || path.trim().length === 0) {
        return { valid: false, reason: 'Path cannot be empty' };
    }

    // Null bytes — could truncate strings in C-based implementations
    if (path.includes('\0')) {
        return { valid: false, reason: 'Path cannot contain null bytes' };
    }

    // Path traversal via `..`
    const segments = path.split('/');
    for (const segment of segments) {
        if (segment === '..') {
            return {
                valid: false,
                reason: 'Path cannot contain ".." (directory traversal)',
            };
        }
    }

    // Control characters (ASCII 1-31)
    for (let i = 0; i < path.length; i++) {
        const code = path.charCodeAt(i);
        if (code >= 1 && code <= 31) {
            return {
                valid: false,
                reason: 'Path cannot contain control characters',
            };
        }
    }

    return { valid: true };
}

/**
 * Sanitize a user-provided path by normalizing redundant separators
 * and removing leading/trailing whitespace.
 *
 * Does NOT validate — call `validateDevicePath` first.
 */
export function sanitizeDevicePath(path: string): string {
    return path.trim().replace(/\/+/g, '/'); // collapse multiple slashes
}
